import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { getHasStateOrProvince } from "@faire/retailer-visitor-shared/lib/getHasStateOrProvince";
import { CANADA_PROVINCE } from "@faire/web--source/common/consts/CANADA_PROVINCE";
import { CANADA_PROVINCE_SHORTHAND } from "@faire/web--source/common/consts/CANADA_PROVINCE_SHORTHAND";
import { ISO_TO_COUNTRY_NAME_WITHOUT_SANCTIONS } from "@faire/web--source/common/consts/ISO_TO_COUNTRY_NAME";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web--source/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { US_STATE } from "@faire/web--source/common/consts/US_STATE";
import { US_STATE_SHORTHAND } from "@faire/web--source/common/consts/US_STATE_SHORTHAND";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web--source/common/localization";
import { Dropdown } from "@faire/web--source/slate/Dropdown";
import { Input } from "@faire/web--source/slate/Input";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { IAddress } from "@faire/web-api--source/indigofair/data/IAddress";
import * as React from "react";
import { useEffect, useCallback } from "react";
import styled from "styled-components";

interface IProps {
  onChange: (address?: Partial<IAddress>) => void;
  address?: Partial<IAddress>;
  error?: string;
  prefillCountry?: boolean;
  disabled?: boolean;
  /** When enabled, will give every country as an option for the country field. */
  showAllCountryOptions?: boolean;
  isOwFApplicationForm?: boolean;
}

export const SimplifiedAddressForm: React.FC<IProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  const { prefillCountry, address, disabled, showAllCountryOptions } = props;
  const { retailerCountry, isEurope } = useNationality();

  const onChange = useCallback(
    (key: keyof IAddress, value: string) => {
      props.onChange({ ...(props.address || {}), ...{ [key]: value } });
    },
    [props]
  );

  useEffect(() => {
    if (prefillCountry && !address && retailerCountry) {
      onChange("country", retailerCountry);
    }
  }, [prefillCountry, address, retailerCountry, onChange]);

  const getError = (key: keyof IAddress) => {
    return !props.address?.[key] ? props.error : undefined;
  };

  const country = props.address?.country;
  const hasStateOrProvince = getHasStateOrProvince(country);
  const stateOrProvince = getStateOrProvince(country);
  const stateOrProvinceShorthand = getStateOrProvinceShorthand(country);
  const countryOptions = showAllCountryOptions
    ? getAllCountryOptions(strictLocalize)
    : getCountryOptions(isEurope, strictLocalize);
  const postalCodePlaceholder = usePostalCodePlaceholder(country);
  const addressLine1Placeholder = useAddressLine1Placeholder(country);
  const cityPlaceholder = useCityPlaceholder(country);
  const statePlaceholder = useStatePlaceholder(country);

  return (
    <Column style={{ width: "100%" }}>
      <AddressRow>
        <AddressFieldWrapper>
          <DropdownStyled
            data-test-id="country"
            value={props.address?.country}
            placeholder={strictLocalize({
              defaultMessage: "Country",
              description: { text: "Field in address form" },
            })}
            options={countryOptions}
            onSelect={(country) => onChange("country", country.value)}
            error={getError("country")}
            disabled={disabled}
          />
        </AddressFieldWrapper>
      </AddressRow>
      <AddressRow>
        <AddressFieldWrapper>
          <Input
            data-test-id="address1"
            name="address1"
            value={props.address?.address1 ?? ""}
            error={getError("address1")}
            placeholder={addressLine1Placeholder}
            onChange={(e) => onChange("address1", e.target.value)}
            disabled={disabled}
          />
        </AddressFieldWrapper>
      </AddressRow>
      <AddressRow>
        <AddressFieldWrapper>
          <Input
            data-test-id="address2"
            name="address2"
            value={props.address?.address2 ?? ""}
            placeholder={strictLocalize({
              defaultMessage: "Apt, Suite, Bldg (optional)",
              description: { text: "Field in address form" },
            })}
            onChange={(e) => onChange("address2", e.target.value)}
            disabled={disabled}
          />
        </AddressFieldWrapper>
      </AddressRow>
      <AddressRow>
        <AddressFieldWrapper>
          <Input
            data-test-id="city"
            name="city"
            value={props.address?.city ?? ""}
            error={getError("city")}
            placeholder={cityPlaceholder}
            onChange={(e) => onChange("city", e.target.value)}
            disabled={disabled}
          />
        </AddressFieldWrapper>
      </AddressRow>
      <AddressRow>
        {hasStateOrProvince ? (
          <>
            <AddressFieldWrapper>
              <DropdownStyled
                data-test-id="state"
                value={props.address?.state}
                placeholder={statePlaceholder}
                error={getError("state")}
                options={
                  stateOrProvince(strictLocalize) ?? stateOrProvinceShorthand
                }
                onSelect={(e) => {
                  onChange("state", e.value);
                }}
                disabled={disabled}
              />
            </AddressFieldWrapper>
            <Spacer width="1x" />
          </>
        ) : null}
        <AddressFieldWrapper>
          <Input
            data-test-id="postal_code"
            name="postal_code"
            value={props.address?.postal_code ?? ""}
            error={getError("postal_code")}
            placeholder={postalCodePlaceholder}
            maxLength={10}
            onChange={(e) => onChange("postal_code", e.target.value)}
            disabled={disabled}
          />
        </AddressFieldWrapper>
      </AddressRow>
    </Column>
  );
};

const getStateOrProvince = (country: string | undefined) => {
  switch (country) {
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN:
      return CANADA_PROVINCE;
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.USA:
    default:
      return US_STATE;
  }
};

const getStateOrProvinceShorthand = (country: string | undefined): string[] => {
  switch (country) {
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN:
      return CANADA_PROVINCE_SHORTHAND;
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.USA:
    default:
      return US_STATE_SHORTHAND;
  }
};

// Similar to 'getCountryOptions' except we are not filtering out any countries
const getAllCountryOptions = (strictLocalize: StrictLocalizeFunction) => {
  const allCountryOptions =
    ISO_TO_COUNTRY_NAME_WITHOUT_SANCTIONS(strictLocalize);
  return Object.entries(allCountryOptions).map(([optionKey, optionLabel]) => ({
    label: optionLabel,
    value: optionKey,
  }));
};

const getCountryOptions = (
  isEurope: boolean,
  strictLocalize: StrictLocalizeFunction
) => {
  const options = isEurope ? COUNTRY_OPTIONS_EU : COUNTRY_OPTIONS_NAM;
  return options.map((iso) => ({
    label: ISO_TO_COUNTRY_NAME_WITHOUT_SANCTIONS(strictLocalize)[iso],
    value: iso,
  }));
};

const usePostalCodePlaceholder = (country: string | undefined) => {
  const { strictLocalize } = useStrictLocalization();
  switch (country) {
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.GBR:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.FRA:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.DEU:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.ESP:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.ITA:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CHE:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.DNK:
      return strictLocalize({
        defaultMessage: "Postal Code",
        description: { text: "Field in address form" },
      });
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.USA:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.SWE:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.NOR:
    default:
      return strictLocalize({
        defaultMessage: "Zip",
        description: { text: "Field in address form, zip code" },
      });
  }
};

const useAddressLine1Placeholder = (country: string | undefined): string => {
  const { strictLocalize } = useStrictLocalization();
  switch (country) {
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.USA:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN:
      return strictLocalize({
        defaultMessage: "Address Line 1",
        description: {
          text: "Field in address form, specifically for North American",
        },
      });
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.GBR:
      return strictLocalize({
        defaultMessage: "Building and Street",
        description: { text: "Field in address form" },
      });
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.FRA:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.DEU:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.ESP:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.ITA:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CHE:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.DNK:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.SWE:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.NOR:
    default:
      return strictLocalize({
        defaultMessage: "Street and Number",
        description: { text: "Field in address form" },
      });
  }
};

const useCityPlaceholder = (country: string | undefined): string => {
  const { strictLocalize } = useStrictLocalization();
  switch (country) {
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.GBR:
      return strictLocalize({
        defaultMessage: "Post Town",
        description: { text: "Field in address form, equivalent to City" },
      });
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.FRA:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.DEU:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.ESP:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.ITA:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CHE:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.DNK:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.SWE:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.NOR:
      return strictLocalize({
        defaultMessage: "Town / Locality",
        description: { text: "Field in address form, equivalent to City" },
      });
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN:
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.USA:
    default:
      return strictLocalize({
        defaultMessage: "City",
        description: { text: "Field in address form" },
      });
  }
};

const useStatePlaceholder = (country: string | undefined): string => {
  const { strictLocalize } = useStrictLocalization();
  switch (country) {
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.USA:
      return strictLocalize({
        defaultMessage: "State",
        description: { text: "Field in address form, like province" },
      });
    case THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN:
      return strictLocalize({
        defaultMessage: "Province/Territory",
        description: { text: "Field in address form" },
      });
    default:
      return strictLocalize({
        defaultMessage: "State/Province",
        description: { text: "Field in address form" },
      });
  }
};

const COUNTRY_OPTIONS_NAM = [
  THREE_LETTER_PRIMARY_COUNTRY_CODES.USA,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.CAN,
];

const COUNTRY_OPTIONS_EU = [
  THREE_LETTER_PRIMARY_COUNTRY_CODES.GBR,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.FRA,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.DEU,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.ESP,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.ITA,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.CHE,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.DNK,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.SWE,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.NOR,
  THREE_LETTER_PRIMARY_COUNTRY_CODES.NLD,
];

const DropdownStyled = styled(Dropdown)`
  width: 100%;
`;

const AddressRow = styled(Row).attrs({ align: "flex-start" })`
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AddressFieldWrapper = styled.div`
  flex: 1;
`;
